/**
 * Care action engine — turns a CareScoreData snapshot into the prioritised
 * "Do next" list. Extracted from CareScorePanel so the workspace command
 * column and any other surface share one source of actions.
 */

import { CareScoreData } from "./hooks/useCareScore";

// ---------------------------------------------------------------------------
// Action system
// ---------------------------------------------------------------------------

export type Impact = "high" | "medium" | "low";
export type Effort = "quick" | "planned";
export type Category =
  | "service"
  | "content"
  | "runway"
  | "contact"
  | "growth"
  | "planning";

export interface Action {
  label: string;
  impact: Impact;
  effort: Effort;
  category: Category;
  link?: string;
}

export function getActions(data: CareScoreData): Action[] {
  const actions: Action[] = [];
  const { details } = data;

  // -- Service (18%) --
  if (details.recentPublishFailures > 0) {
    actions.push({
      label: `${details.recentPublishFailures} publishing failure${details.recentPublishFailures > 1 ? "s" : ""} in the last 30 days, check social accounts`,
      impact: "high",
      effort: "quick",
      category: "service",
      link: "home/setup",
    });
  }
  if (!details.hasCurrentReport && !details.hasPrevReport) {
    actions.push({
      label: "Generate a monthly social media report (none on record)",
      impact: "high",
      effort: "quick",
      category: "service",
      link: "reporting",
    });
  } else if (!details.hasCurrentReport) {
    actions.push({
      label: "Generate this month's report",
      impact: "medium",
      effort: "quick",
      category: "service",
      link: "reporting",
    });
  }
  if (details.obligationsTotal === 0) {
    actions.push({
      label: "Set up service obligations (monthly calls, report deliveries)",
      impact: "high",
      effort: "planned",
      category: "service",
      link: "home/obligations",
    });
  } else {
    const overdue = details.obligationsTotal - details.obligationsOnTime;
    if (overdue > 0) {
      actions.push({
        label: `Complete ${overdue} overdue obligation${overdue > 1 ? "s" : ""}`,
        impact: "high",
        effort: "quick",
        category: "service",
        link: "home/obligations",
      });
    }
  }

  // -- Content (20%) --
  if (details.captionlessReadyPosts > 0) {
    actions.push({
      label: `${details.captionlessReadyPosts} ready/scheduled post${details.captionlessReadyPosts > 1 ? "s" : ""} missing captions`,
      impact: "high",
      effort: "quick",
      category: "content",
      link: "posts",
    });
  }
  if (details.totalMedia === 0) {
    actions.push({
      label: "Upload photos or videos to start building content",
      impact: "high",
      effort: "planned",
      category: "content",
      link: "library/photos",
    });
  }
  if (details.publishedPosts30d === 0) {
    actions.push({
      label: "Publish a post (none in the last 30 days)",
      impact: "high",
      effort: "quick",
      category: "content",
      link: "posts",
    });
  }
  if (details.recentPostsCount === 0 && details.publishedPosts30d > 0) {
    actions.push({
      label: "Create a post (none created in the last 30 days)",
      impact: "medium",
      effort: "quick",
      category: "content",
      link: "posts",
    });
  }
  if (details.scheduledPostsCount === 0) {
    actions.push({
      label: "Schedule upcoming posts (nothing in the queue)",
      impact: "medium",
      effort: "quick",
      category: "content",
      link: "grid",
    });
  }
  if (details.pipelineCount === 0 && details.totalMedia > 0) {
    actions.push({
      label: "No media in approval pipeline, review draft content",
      impact: "medium",
      effort: "planned",
      category: "content",
      link: "library/photos",
    });
  }
  if (
    details.totalMedia > 0 &&
    details.utilizationRate < 40
  ) {
    actions.push({
      label: `Content utilization at ${details.utilizationRate}%, review and approve more drafts`,
      impact: "medium",
      effort: "planned",
      category: "content",
      link: "library/photos",
    });
  }

  // -- Runway (28%) --
  if (!details.postsPerWeekSet) {
    actions.push({
      label: "Set posts-per-week target in client settings",
      impact: "medium",
      effort: "quick",
      category: "runway",
    });
  } else {
    if (details.firstShortWeek) {
      const sw = details.firstShortWeek;
      const isThisWeek = details.weeklyCoverage?.[0]?.weekStart === sw.weekStart;
      const impact: Impact = isThisWeek ? "high" : sw.count === 0 ? "high" : "medium";
      actions.push({
        label: sw.count === 0
          ? `No content scheduled for week of ${sw.weekStart}`
          : `${sw.count} of ${sw.target} posts covered week of ${sw.weekStart}`,
        impact,
        effort: "planned",
        category: "runway",
        link: "grid",
      });
    }
    if (details.readyPostsCount === 0 && details.runwayWeeks < 3) {
      actions.push({
        label: "No posts in the ready queue for future scheduling",
        impact: details.runwayWeeks < 1 ? "high" : "medium",
        effort: "planned",
        category: "runway",
        link: "posts",
      });
    }
    if (details.maxGapDays > 0 && details.postsPerWeek > 0) {
      const idealGap = 7 / details.postsPerWeek;
      if (details.maxGapDays > idealGap * 2) {
        actions.push({
          label: `${details.maxGapDays}d gap between scheduled posts, redistribute for even coverage`,
          impact: "medium",
          effort: "quick",
          category: "runway",
          link: "grid",
        });
      }
    }
    if (details.captionlessScheduled > 0) {
      actions.push({
        label: `${details.captionlessScheduled} scheduled post${details.captionlessScheduled > 1 ? "s" : ""} missing captions, not ready to publish`,
        impact: "high",
        effort: "quick",
        category: "runway",
        link: "posts",
      });
    }
    if (details.calendarEventsUncovered > 0) {
      const eventNames: string[] = details.uncoveredEventNames || [];
      const namesList = eventNames.length > 0
        ? eventNames.join(", ")
        : `${details.calendarEventsUncovered} event${details.calendarEventsUncovered > 1 ? "s" : ""}`;
      actions.push({
        label: `No content planned for: ${namesList}`,
        impact: "medium",
        effort: "planned",
        category: "runway",
        link: "calendar",
      });
    }
  }

  // -- Contact (13%) --
  if (details.daysSinceLastContact < 0) {
    actions.push({
      label: "Log a client contact (no record yet)",
      impact: "high",
      effort: "quick",
      category: "contact",
    });
  } else if (details.daysSinceLastContact > 30) {
    actions.push({
      label: `No contact in ${details.daysSinceLastContact} days, reach out now`,
      impact: "high",
      effort: "quick",
      category: "contact",
    });
  } else if (details.daysSinceLastContact > 14) {
    actions.push({
      label: `Last contact ${details.daysSinceLastContact}d ago, send a check-in`,
      impact: "medium",
      effort: "quick",
      category: "contact",
    });
  } else if (details.daysSinceLastContact > 7) {
    actions.push({
      label: `Last contact ${details.daysSinceLastContact}d ago, consider a quick update`,
      impact: "low",
      effort: "quick",
      category: "contact",
    });
  }

  // -- Growth (9%) --
  if (details.currentFollowers === 0) {
    actions.push({
      label: "Connect social accounts to track follower growth",
      impact: "medium",
      effort: "planned",
      category: "growth",
      link: "home/setup",
    });
  } else if (details.followerGrowth < 0) {
    actions.push({
      label: `Followers down ${details.followerGrowthPct}% this month, review content strategy`,
      impact: "medium",
      effort: "planned",
      category: "growth",
      link: "reporting",
    });
  }
  if (
    details.publishedPosts30d > 0 &&
    details.postsWithEngagement === 0
  ) {
    actions.push({
      label: "Engagement data missing, check social account connections",
      impact: "medium",
      effort: "quick",
      category: "growth",
      link: "home/setup",
    });
  } else if (details.avgEngagement > 0 && details.avgEngagement < 30) {
    actions.push({
      label: `Avg engagement is ${details.avgEngagement}, try reels or carousel posts`,
      impact: "low",
      effort: "planned",
      category: "growth",
      link: "library/reels",
    });
  }

  // -- Planning (12%) --
  if (details.staleApprovals > 0) {
    actions.push({
      label: `${details.staleApprovals} approval${details.staleApprovals > 1 ? "s" : ""} pending over 7 days, follow up with client`,
      impact: "high",
      effort: "quick",
      category: "planning",
      link: "posts",
    });
  }
  if (
    details.approvalReadyPosts > 0 &&
    details.scheduledPostsCount === 0
  ) {
    actions.push({
      label: `${details.approvalReadyPosts} post${details.approvalReadyPosts > 1 ? "s" : ""} ready, schedule them`,
      impact: "high",
      effort: "quick",
      category: "planning",
      link: "grid",
    });
  }
  if (details.activeBriefs === 0) {
    actions.push({
      label: "Book a photographer session (create a brief)",
      impact: "medium",
      effort: "planned",
      category: "planning",
      link: "briefs",
    });
  } else if (details.briefFreshness === "stale") {
    actions.push({
      label: "Active brief is getting stale, update or complete it",
      impact: "medium",
      effort: "quick",
      category: "planning",
      link: "briefs",
    });
  }
  if (details.ideaRejectionRate > 50) {
    actions.push({
      label: `${details.ideaRejectionRate}% idea rejection rate, revisit content direction with client`,
      impact: "high",
      effort: "planned",
      category: "planning",
      link: "ideas",
    });
  }
  if (details.avgApprovalDays > 7) {
    actions.push({
      label: `Avg approval takes ${details.avgApprovalDays}d, consider streamlining the review process`,
      impact: "low",
      effort: "planned",
      category: "planning",
    });
  }
  if (
    details.approvedIdeas === 0 &&
    details.ideasInPipeline === 0
  ) {
    actions.push({
      label: "Add content ideas to the pipeline",
      impact: "medium",
      effort: "planned",
      category: "planning",
      link: "ideas",
    });
  } else if (
    details.approvedIdeas > 0 &&
    details.approvalReadyPosts === 0
  ) {
    actions.push({
      label: `${details.approvedIdeas} approved idea${details.approvedIdeas > 1 ? "s" : ""}, turn them into posts`,
      impact: "medium",
      effort: "planned",
      category: "planning",
      link: "posts",
    });
  }

  // Sort: high > medium > low, within same impact quick > planned
  const impactOrder: Record<Impact, number> = {
    high: 0,
    medium: 1,
    low: 2,
  };
  const effortOrder: Record<Effort, number> = { quick: 0, planned: 1 };
  return actions.sort(
    (a, b) =>
      impactOrder[a.impact] - impactOrder[b.impact] ||
      effortOrder[a.effort] - effortOrder[b.effort],
  );
}
